// Where the link between a client and their photos lives (browser side).
//
// A listing is public (anybody can list all published galleries without a login), and so is a
// user's `publicData`. So a gallery's title is neutral and the client's name and account id are
// kept in the listing's `privateData`, which only the photographer (the listing's author) can
// read. Keep in sync with server/api-util/clientData.js.
//
// Listings made before this change still carry the old public fields; the helpers fall back to
// them until the one-time migration has run.

export const NEUTRAL_TITLE = 'Photo gallery';
export const UNCLAIMED_TITLE = 'New Shoot';

const attributesOf = listing => (listing && listing.attributes) || {};

/** True for an "on location" spot (a listing that is not a gallery). */
export const isSpotListing = listing => attributesOf(listing).publicData?.isSpot === true;

/** A listings query response (SDK shape) without the spots, for every list of galleries. */
export const withoutSpots = response => {
  const listings = response?.data?.data;
  if (!Array.isArray(listings)) {
    return response;
  }
  return { ...response, data: { ...response.data, data: listings.filter(l => !isSpotListing(l)) } };
};

/** The client's account id (only visible to the photographer), or undefined. */
export const listingCustomerId = listing => {
  const { privateData, publicData } = attributesOf(listing);
  return (privateData && privateData.customer) || (publicData && publicData.customer) || undefined;
};

/** The client's full name (only visible to the photographer), or '' when unknown. */
export const listingClientName = listing => {
  const { privateData, publicData, title } = attributesOf(listing);
  // An "on location" spot is not a gallery: its title is not a client's name.
  if (publicData && publicData.isSpot === true) {
    return '';
  }
  if (privateData && privateData.clientName) {
    return String(privateData.clientName);
  }
  const legacy = String(title || '').trim();
  return legacy && legacy !== NEUTRAL_TITLE && legacy !== UNCLAIMED_TITLE ? legacy : '';
};

/** The signed-in user's "your photos are ready" entries (private; the old public ones as fallback). */
export const userNotifications = user => {
  const profile = (user && user.attributes && user.attributes.profile) || {};
  return (
    (profile.privateData && profile.privateData.notifications) ||
    (profile.publicData && profile.publicData.notifications) ||
    []
  );
};
